import { dynamicString } from '@home/utils/dynamicString';
import { getName } from '@home/utils/common';
import type { ComponentMetricsItemType } from '@/pages/dashboard/components/PageContainer/type';

/**
 * 指标信息项类型
 * 用于存储单个指标的多语言名称和格式化规则
 */
export interface IMetricInfoItem {
  /** 英文名称 */
  en?: string;
  /** 中文名称 */
  cn?: string;
  /** 格式化规则 */
  formatter?: string;
  /** 其他属性 */
  [key: string]: string | undefined;
}

/**
 * 指标信息对象类型
 * key 为指标标识，value 为指标信息项
 */
export type TMetricsInfo = Record<string, IMetricInfoItem>;

/**
 * 动态字符串数据源类型
 * 用于模板字符串解析
 */
export interface IMetricsDataSource {
  /** 指标信息对象 */
  metricsInfo?: TMetricsInfo;
  /** 其他数据源 */
  [key: string]: unknown;
}

/**
 * 解析配置字符串（支持模板字符串）
 * @param value - 配置值（可能包含模板字符串如 {{metricsInfo.xxx.en}}）
 * @param metricsInfo - 指标信息对象
 * @param defaultValue - 默认值，当 value 为空时返回
 * @returns 解析后的字符串
 */
export const resolveConfigString = (
  value?: string,
  metricsInfo?: TMetricsInfo,
  defaultValue = ''
): string => {
  if (!value) {
    return defaultValue;
  }
  
  if (!metricsInfo) {
    return value;
  }
  
  const metricsDataSource = prepareMetricsDataSource(metricsInfo);
  return dynamicString(value, metricsDataSource, defaultValue);
};

/**
 * 根据 isSingle 选择对应的指标名称
 * @param isSingle - 是否为单日期
 * @param singleName - 单日期名称
 * @param rangeName - 多日期范围名称
 * @param fallbackName - 回退名称（当 rangeName 不存在时使用）
 * @returns 选中的名称
 */
export const selectMetricNameByDateRange = (
  isSingle: boolean,
  singleName: string,
  rangeName?: string,
  fallbackName?: string
): string => {
  if (isSingle) {
    return singleName || fallbackName || '';
  }
  return rangeName || singleName || fallbackName || '';
};

/**
 * 解析并选择国际化名称（支持模板字符串）
 * 先解析 en 和 cn 的模板字符串，再根据语言选择
 * @param isEn - 是否为英文
 * @param enName - 英文名称（可能包含模板字符串）
 * @param cnName - 中文名称（可能包含模板字符串）
 * @param metricsInfo - 指标信息对象（用于解析模板字符串）
 * @param additionalData - 额外的数据源（用于解析模板字符串，如 tabMsg）
 * @param defaultValue - 默认值，当解析结果为空时返回
 * @returns 根据语言选择后的名称
 */
export const resolveAndSelectName = (
  isEn: boolean,
  enName?: string,
  cnName?: string,
  metricsInfo?: TMetricsInfo,
  additionalData?: Record<string, unknown>,
  defaultValue = ''
): string => {
  // 准备动态字符串的数据源
  const dynamicData = prepareMetricsDataSource(metricsInfo, additionalData);

  // 先解析模板字符串
  const resolvedEnName = enName ? dynamicString(enName, dynamicData, '') : '';
  const resolvedCnName = cnName ? dynamicString(cnName, dynamicData, '') : '';

  // 再根据语言选择
  const selectedName = getName(isEn, resolvedEnName, resolvedCnName);

  return selectedName || defaultValue;
};

/**
 * 准备动态字符串的数据源
 * @param metricsInfo - 指标信息对象
 * @param additionalData - 额外的数据源
 * @returns 数据源对象
 */
export const prepareMetricsDataSource = (
  metricsInfo?: TMetricsInfo,
  additionalData?: Record<string, unknown>
): IMetricsDataSource => {
  const dataSource: IMetricsDataSource = {};
  if (metricsInfo) {
    dataSource.metricsInfo = metricsInfo;
  }
  if (additionalData) {
    Object.assign(dataSource, additionalData);
  }
  return dataSource;
};

/**
 * 自定义名称配置类型
 */
export interface ICustomName {
  /** 英文名称 */
  enName?: string;
  /** 中文名称 */
  cnName?: string;
}

/**
 * 解析并选择 header 名称（支持配置的指标名称和自定义名称）
 * @param isEn - 是否为英文
 * @param defaultHeader - 默认 header 名称
 * @param metricEnName - 配置的英文指标名称（可能包含模板字符串）
 * @param metricCnName - 配置的中文指标名称（可能包含模板字符串）
 * @param customName - 自定义名称对象 { enName, cnName }
 * @param metricsInfo - 指标信息对象（用于解析模板字符串）
 * @returns 解析后的 header 名称
 */
export const resolveHeaderName = (
  isEn: boolean,
  defaultHeader: string,
  metricEnName?: string,
  metricCnName?: string,
  customName?: ICustomName,
  metricsInfo?: TMetricsInfo
): string => {
  // 优先级：metricEnName/metricCnName > customName > defaultHeader
  if (metricEnName || metricCnName) {
    const metricsDataSource = prepareMetricsDataSource(metricsInfo);
    const resolvedEnName = metricEnName ? dynamicString(metricEnName, metricsDataSource, '') : '';
    const resolvedCnName = metricCnName ? dynamicString(metricCnName, metricsDataSource, '') : '';
    return resolveAndSelectName(isEn, resolvedEnName, resolvedCnName, metricsInfo, undefined, defaultHeader);
  }
  
  if (customName) {
    return isEn ? (customName.enName || defaultHeader) : (customName.cnName || defaultHeader);
  }
  
  return defaultHeader;
};

/**
 * tableMetric 配置：扁平数组（全周期共用）或按 timeType / 自定义 key 分组
 * @example
 * // 兼容旧配置
 * tableMetric: ['total_users', 'new_users']
 * // 按日周月（及其他 timeType）分别配置
 * tableMetric: { Day: [...], Week: [...], Month: [...], default: [...] }
 */
export type TTableMetricConfig = string[] | Record<string, string[]>;

/** 对象形态 tableMetric 在无匹配 timeType 时的兜底 key（按顺序尝试） */
const TABLE_METRIC_FALLBACK_KEYS = ['default', 'all'] as const;

/**
 * 将 tableMetric 解析为当前 timeType 下的指标 key 列表
 * - 数组：原样返回（兼容旧逻辑）
 * - 对象：优先取 tableMetric[timeType]，否则尝试 default / all
 * - 无有效配置：返回 undefined，表示不做筛选（展示 componentMetrics 全部）
 */
export const resolveTableMetric = (
  tableMetric?: TTableMetricConfig,
  timeType?: string,
): string[] | undefined => {
  if (!tableMetric) {
    return undefined;
  }
  if (Array.isArray(tableMetric)) {
    return tableMetric.length > 0 ? tableMetric : undefined;
  }
  if (timeType) {
    const byTimeType = tableMetric[timeType];
    if (Array.isArray(byTimeType) && byTimeType.length > 0) {
      return byTimeType;
    }
  }
  for (const key of TABLE_METRIC_FALLBACK_KEYS) {
    const fallback = tableMetric[key];
    if (Array.isArray(fallback) && fallback.length > 0) {
      return fallback;
    }
  }
  return undefined;
};

/**
 * 根据 tableMetric 配置筛选并排序指标列表（支持按 timeType 解析对象配置）
 */
export const filterComponentMetricsByTableMetric = (
  metrics: ComponentMetricsItemType[],
  tableMetric?: TTableMetricConfig,
  timeType?: string,
): ComponentMetricsItemType[] => {
  const metricKeys = resolveTableMetric(tableMetric, timeType);
  if (!metricKeys || metricKeys.length === 0) {
    return metrics || [];
  }
  const metricsMap = new Map<string, ComponentMetricsItemType>();
  (metrics || []).forEach(metric => {
    const key = metric.reqMetricKey || metric.metricKey;
    if (key) {
      metricsMap.set(key, metric);
    }
  });
  return metricKeys
    .map(key => metricsMap.get(key))
    .filter((metric): metric is ComponentMetricsItemType => metric !== undefined);
};

/** tabList 按 timeType 解析（数组或 Record，支持 default / all 兜底） */
export const resolveTabListByTimeType = <T>(
  tabList?: T[] | Record<string, T[]>,
  timeType?: string,
): T[] => {
  if (!tabList) {
    return [];
  }
  if (Array.isArray(tabList)) {
    return tabList;
  }
  if (timeType) {
    const byTimeType = tabList[timeType];
    if (Array.isArray(byTimeType) && byTimeType.length > 0) {
      return byTimeType;
    }
  }
  for (const key of TABLE_METRIC_FALLBACK_KEYS) {
    const fallback = tabList[key];
    if (Array.isArray(fallback) && fallback.length > 0) {
      return fallback;
    }
  }
  return [];
};

type TSegmentMetricLike = {
  resName?: string;
  indicatorsName?: string;
  id?: string;
};

/**
 * Segment 指标 tab 筛选（按 resName / indicatorsName / id 匹配，支持 tabMetric 日周月配置）
 */
export const filterSegmentMetricsByTabMetric = <T extends TSegmentMetricLike>(
  metrics: T[],
  tabMetric?: TTableMetricConfig,
  timeType?: string,
): T[] => {
  const metricKeys = resolveTableMetric(tabMetric, timeType);
  if (!metricKeys || metricKeys.length === 0) {
    return metrics || [];
  }
  const metricsMap = new Map<string, T>();
  (metrics || []).forEach(item => {
    const keys = [item.resName, item.indicatorsName, item.id].filter(Boolean) as string[];
    keys.forEach(key => {
      if (!metricsMap.has(key)) {
        metricsMap.set(key, item);
      }
    });
  });
  return metricKeys
    .map(key => metricsMap.get(key))
    .filter((item): item is T => item !== undefined);
};

